/**
 * BP-6 Cloudflare Turnstile wiring — Managed widget, vanilla DOM (no React).
 * Adapted from cellavia-dental's `ConsultationForm.tsx` desen (render + reset,
 * same site key / same merkez contract, same "on-submit execute" idea).
 *
 * MODE (2026-07-23, ikinci düzeltme turu — CF API'den kesin bulgu): sitekey
 * `0x4AAAAAAD4zAy13OTVA7bQ5` (cellavia/dentvisit/thenexwell ortak
 * bp6-lead-intake widget'ı) dashboard'da **"mode":"managed"** olarak kayıtlı —
 * "Invisible" DEĞİL. Bu dosyanın İLK versiyonu (M2, aynı gün, ilk revizyon
 * turu) `size: "invisible"` render seçeneğini kullanıyordu; bu, managed bir
 * sitekey ile GEÇERSİZ bir kombinasyon (widget-tipi dashboard'da sabit,
 * runtime `size` parametresiyle değiştirilemez). Sitekey'in moduna
 * DOKUNULMUYOR (paylaşılan widget, üç site tarafından kullanılıyor) — kod bu
 * gerçeğe uyarlandı:
 *   - `size: "invisible"` KALDIRILDI (managed'da geçersiz/anlamsız).
 *   - `execution: "execute"` KORUNDU — managed widget'larda da geçerli bir
 *     parametre; challenge'ı `render()` anında değil, `turnstile.execute()`
 *     çağrılana kadar ERTELER (aynı "submit anında tetikle" niyeti).
 *   - `appearance: "interaction-only"` EKLENDİ — managed widget'ın normalde
 *     her zaman gösterdiği "I'm not a robot" kutusunu, yalnızca Cloudflare
 *     GERÇEKTEN bir etkileşim (checkbox/challenge) istediğinde görünür kılar;
 *     çoğu ziyaretçi için widget hiç görünmeden (arka planda) çözülür — bu,
 *     eski `size:"invisible"` niyetine en yakın managed-mode karşılığı.
 *
 * Önceki (invisible) yorum notu (2026-07-23, ilk revizyon turu, M2) burada
 * artık geçerli DEĞİL — `appearance: "execute"` hatası zaten `execution`
 * parametresiyle düzeltilmişti; bu ikinci tur sadece `size`/`appearance`
 * kombinasyonunu managed moda uyarlıyor, `execution` aynı kalıyor.
 *
 * ALWAYS ACTIVE (2026-09-04, ADIM 1 — plan §2.1: "yeni araçta BP6 flag'ine
 * bağlı değil, her zaman açık olmalı — dosya yükleyen public endpoint,
 * spam/abuse yüzeyi"). Kaynak repodaki `BP6_MODE` koşullu davranışı bu
 * dosyada TAMAMEN kaldırıldı; script her zaman yüklenir, token her zaman
 * alınır. Eski `medikal-test-robotu`'ndaki BP6_MODE-gated hali için o
 * reponun `src/turnstile.ts`'ine bakılabilir.
 */
import { TURNSTILE_SITE_KEY } from "./config";

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      execute: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/**
 * "Sessiz" (etkileşimsiz, arka planda otomatik çözülen) yol için zaman aşımı —
 * brief §İş 1'in orijinal "8sn" talebi, managed modda da varsayılan/mutlu-yol
 * bütçesi olarak korunuyor.
 */
export const SILENT_TIMEOUT_MS = 8000;

/**
 * `before-interactive-callback` tetiklenirse (Cloudflare ziyaretçiye GERÇEKTEN
 * bir kutu/checkbox göstermeye karar verdiyse) zaman aşımı bu değere uzatılır
 * — 8sn bir insanın checkbox'a tıklaması için gerçekçi değil (2026-07-23,
 * ikinci revizyon turu, madde 3).
 */
export const INTERACTIVE_TIMEOUT_MS = 60000;

/**
 * Mutlak üst sınır (2026-09-04, görsel QA sırasında bulunan gerçek kilitlenme —
 * ADIM 3 review notu). `onExtend` (`before-interactive-callback`) sınırsız
 * çağrılabiliyordu ve her çağrıda zaman aşımını yeniden INTERACTIVE_TIMEOUT_MS'e
 * kuruyordu. Yanlış yapılandırılmış bir sitekey (ör. domain izinli değil —
 * canlıda görülen "Error: 110200") Cloudflare'in kendi widget'ının sessizce
 * tekrar tekrar denemesine yol açıyor; her deneme `before-interactive-callback`
 * tetikleyip zamanlayıcıyı resetliyor ve `getTurnstileToken` promise'ı ASLA
 * settle olmuyordu — kullanıcı "Uploading…" ekranında kalıcı olarak kilitli
 * kalıyordu. Bu sabit, kaç kez uzatılırsa uzatılsın toplam bekleme süresini
 * sınırlar.
 */
export const ABSOLUTE_TIMEOUT_MS = 90000;

let scriptPromise: Promise<void> | null = null;
let widgetId: string | undefined;
let widgetContainer: HTMLElement | null = null;
let pendingResolvers: Array<(token: string) => void> = [];
let pendingRejecters: Array<(err: Error) => void> = [];
/** `before-interactive-callback` sinyalini o an bekleyen `getTurnstileToken` çağrısına (varsa) iletir. */
let pendingExtenders: Array<() => void> = [];

function onToken(token: string): void {
  const resolvers = pendingResolvers;
  pendingResolvers = [];
  pendingRejecters = [];
  pendingExtenders = [];
  resolvers.forEach((resolve) => resolve(token));
}

function onWidgetError(): void {
  const rejecters = pendingRejecters;
  pendingResolvers = [];
  pendingRejecters = [];
  pendingExtenders = [];
  rejecters.forEach((reject) => reject(new Error("turnstile-widget-error")));
}

/**
 * Cloudflare "widget etkileşime geçmek üzere" sinyali — managed+interaction-only
 * modda ziyaretçiye görünür bir checkbox/challenge açılmadan HEMEN önce ateşlenir.
 * Bekleyen `getTurnstileToken` çağrısının timeout'unu uzatır (madde 3).
 */
function onBeforeInteractive(): void {
  pendingExtenders.forEach((extend) => extend());
}

/** Cloudflare script'ini yalnızca bir kez yükler. */
function loadScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("turnstile: no DOM available"));
      return;
    }
    if (window.turnstile) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("turnstile: script load failed"));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

/**
 * Widget'ı (yoksa) container'a monte eder — MANAGED mod, `execution: "execute"`
 * ile challenge submit anına ertelenir, `appearance: "interaction-only"` ile
 * gerçek etkileşim gerekmedikçe hiç görünmez (bkz. dosya başı yorumu).
 * `dil değişimi (lang switch) → main.ts tüm DOM'u yeniden render eder → eski
 * container DOM'dan kopar ama window.turnstile içindeki eski widget instance'ı
 * hayatta kalırdı (orphan) — yeni bir container geldiğinde önce eskisini
 * `remove()` ile temizliyoruz (ilk revizyon turu, m4 — davranış korunuyor).
 */
async function ensureWidget(container: HTMLElement): Promise<string> {
  await loadScript();
  if (!window.turnstile) throw new Error("turnstile: window.turnstile unavailable after script load");
  if (widgetId && widgetContainer === container) return widgetId;
  if (widgetId) window.turnstile.remove(widgetId);
  widgetContainer = container;
  widgetId = window.turnstile.render(container, {
    sitekey: TURNSTILE_SITE_KEY,
    execution: "execute",
    appearance: "interaction-only",
    callback: onToken,
    "error-callback": onWidgetError,
    "expired-callback": onWidgetError,
    "before-interactive-callback": onBeforeInteractive,
  });
  return widgetId;
}

/**
 * Widget'ı hazırlar + `execute()` çağırır + token'ı bekler (timeout'lu, madde 3
 * uyarınca uzatılabilir). Her zaman çağrılmalı — bu araçta Turnstile
 * koşulsuz açık (plan §2.1). Hata/timeout'ta reject eder; çağıran (main.ts)
 * bu durumda lead'i ASLA token'sız POST'lamamalı (403 garantili) —
 * kullanıcıya nazik hata + tekrar dene göstermeli.
 */
export async function getTurnstileToken(container: HTMLElement): Promise<string> {
  const id = await ensureWidget(container);

  return new Promise<string>((resolve, reject) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout>;
    // Kaç kez extend edilirse edilsin aşılamayacak mutlak sınır — bkz.
    // ABSOLUTE_TIMEOUT_MS yorumu.
    const deadline = Date.now() + ABSOLUTE_TIMEOUT_MS;

    const clearPending = () => {
      pendingResolvers = pendingResolvers.filter((r) => r !== onResolve);
      pendingRejecters = pendingRejecters.filter((r) => r !== onReject);
      pendingExtenders = pendingExtenders.filter((e) => e !== onExtend);
    };

    const startTimer = (ms: number) => {
      clearTimeout(timer);
      const clamped = Math.max(0, Math.min(ms, deadline - Date.now()));
      timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        clearPending();
        reject(new Error("turnstile: timeout"));
      }, clamped);
    };

    const onResolve = (token: string) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      clearPending();
      resolve(token);
    };
    const onReject = (err: Error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      clearPending();
      reject(err);
    };
    // Cloudflare gerçek bir etkileşim (checkbox/challenge) göstermeye karar
    // verdi — 8sn'lik sessiz-yol bütçesi yetersiz kalır, 60sn'e uzat (madde 3).
    // `startTimer` süreyi ABSOLUTE_TIMEOUT_MS deadline'ına göre otomatik
    // kırptığı için, bu kaç kez çağrılırsa çağrılsın toplam bekleme
    // ABSOLUTE_TIMEOUT_MS'i aşamaz.
    const onExtend = () => {
      if (settled) return;
      startTimer(INTERACTIVE_TIMEOUT_MS);
    };

    pendingResolvers.push(onResolve);
    pendingRejecters.push(onReject);
    pendingExtenders.push(onExtend);
    startTimer(SILENT_TIMEOUT_MS);

    try {
      window.turnstile!.execute(id);
    } catch (err) {
      onReject(err instanceof Error ? err : new Error("turnstile: execute failed"));
    }
  });
}

/**
 * Token'lar tek kullanımlıktır — her deneme sonrası (başarı ya da hata) sıfırla
 * ki bir sonraki submit her zaman taze bir challenge/token gerektirsin.
 */
export function resetTurnstile(): void {
  if (widgetId && window.turnstile) window.turnstile.reset(widgetId);
}
